import { Spacing } from '@persona-id/design-tokens';
import { keyBy } from 'lodash/fp';
import { RefObject, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import EditorNavItemGroup from 'dashboard/apps/InquiryTemplateEditor/EditorNavItemGroup';
import EditorNavItemScroll from 'dashboard/apps/InquiryTemplateEditor/EditorNavItemGroup/EditorNavItemScroll';
import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import { PANEL_WIDTH } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { generatePages } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/pages';
import {
  CombinedStepForTree,
  PreviewPageMap,
  StepPreviewMap,
  VerificationTemplateVersionMap,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { BiometricsConsentTextBanners } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateJsonVersionDiff/InquiryTemplateJsonVersionDiff';
import { SelectedStep } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/InquiryTemplateScreenEditor';
import { mapStepsConfigToStepsPreview } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/utils';
import { NavigationItemsContainer } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/shared';
import InquiryTemplateVersionDiffField from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffField';
import InquiryTemplateVersionDiffGeneral from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffGeneral';
import InquiryTemplateVersionDiffSteps from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffSteps';
import InquiryTemplateVersionDiffSummary from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffSummary';
import InquiryTemplateVersionDiffVerification from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffVerification';
import InquiryTemplateVersionDiffWorkflow from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVersionDiffWorkflow';
import {
  BaseInquiryTemplateVersionData,
  VersionDiff,
  VersionDiffLinkedWorkflowStep,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/types';
import {
  buildDiffTreeAndGraph,
  extractActionsAndConditionalNodes,
  VersionDiffNavigationContext,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateVisualVersionDiff/utils';
import { InquiryTemplateVersion } from 'dashboard/models/inquiry-template-version';
import { WorkflowSchema } from 'dashboard/models/workflow-schema';
import { WorkflowStepType } from 'dashboard/models/workflow-version';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';

const LeftColumn = styled.div`
  width: ${PANEL_WIDTH}px;
  flex: none;
  overflow-y: auto;
  overflow-x: hidden;
  background-color: ${({ theme }) => theme.color.background.primary};
  border-left: 1px solid ${({ theme }) => theme.color.border.primary};
  border-right: 1px solid ${({ theme }) => theme.color.border.primary};
  height: 100%;
  position: fixed;
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex-grow: 1;
  flex: auto;
  width: 100%;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.color.background.secondary};
  margin-left: 250px;
`;

const ColumnContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
`;

const NavigationDiv = styled.div`
  scroll-margin-top: 75px;
  padding: ${Spacing[5]};
`;

// Remove any conditional actions or update next step actions
const filterUpdateNextStepAndConditionalActions = (
  workflowSteps: VersionDiffLinkedWorkflowStep[],
) =>
  workflowSteps.filter(
    (linkedWorkflowStep) =>
      !linkedWorkflowStep.workflowStep.isUpdateNextStepAction &&
      linkedWorkflowStep.workflowStep.type !== WorkflowStepType.Conditional,
  );

export interface InquiryTemplateVisualVersionDiffProps {
  versionDiff: VersionDiff;
  inquiryTemplateVersion: InquiryTemplateVersion;
  workflowVersionMap: WorkflowVersionMap;
  workflowSchema: WorkflowSchema;
  verificationTemplateVersionMap: VerificationTemplateVersionMap;
  stepsPreviewMap: StepPreviewMap;
  previewPageMap: PreviewPageMap;
  currentLocale: InquiryIso6391LanguageCode;
}
const InquiryTemplateVisualVersionDiff = ({
  versionDiff,
  inquiryTemplateVersion,
  workflowVersionMap,
  workflowSchema,
  stepsPreviewMap,
  verificationTemplateVersionMap,
  previewPageMap,
  currentLocale,
}: InquiryTemplateVisualVersionDiffProps) => {
  const summaryViewRef = useRef<HTMLDivElement>(null);
  const generalViewRef = useRef<HTMLDivElement>(null);
  const stepsViewRef = useRef<HTMLDivElement>(null);
  const fieldViewRef = useRef<HTMLDivElement>(null);
  const verificationTemplateVersionIdToRefMap = useRef<Record<string, RefObject<HTMLDivElement>>>(
    {},
  );
  const actionSequenceTokenToRefMap = useRef<Record<string, RefObject<HTMLDivElement>>>({});
  const rightColumnRef = useRef<HTMLDivElement>(null);
  const [activeScrollItemId, setActiveScrollItemId] = useState<string>('');
  const [selectedStepDifferStep, setSelectedStepDifferStep] = useState<SelectedStep | null>(null);

  const { steps } = inquiryTemplateVersion.attributes;

  const pages = useMemo(() => Object.values(previewPageMap), [previewPageMap]);
  const isITEInternal = useIsITEInternal();

  const [targetStepsTree, targetExtractedWorkflowSteps] = useMemo<
    [CombinedStepForTree[], VersionDiffLinkedWorkflowStep[]]
  >(
    () => {
      const targetExtractedWorkflowSteps = extractActionsAndConditionalNodes(workflowVersionMap);
      const [targetStepsTree] = buildDiffTreeAndGraph({
        steps,
        stepsPreviewMap,
        previewPageMap,
        extractedWorkflowSteps: targetExtractedWorkflowSteps,
        isITEInternal, // hides verify with persona branches, not needed in dependencies since toggling ITE internal switch causes reload anyways
      });
      return [targetStepsTree, targetExtractedWorkflowSteps];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pages, workflowVersionMap, stepsPreviewMap, steps],
  );

  const { baseStepsTree, baseExtractedWorkflowSteps, baseStepsPreviewMap, basePreviewPageMap } =
    useMemo<BaseInquiryTemplateVersionMetadata>(
      () =>
        generateBaseInquiryTemplateVersionMetadata(
          versionDiff?.baseInquiryTemplateVersionData,
          currentLocale,
          isITEInternal,
        ),
      [versionDiff, currentLocale],
    );

  const targetActionSequences = useMemo(
    () => filterUpdateNextStepAndConditionalActions(targetExtractedWorkflowSteps),
    [targetExtractedWorkflowSteps],
  );
  const baseActionSequences = useMemo(
    () => filterUpdateNextStepAndConditionalActions(baseExtractedWorkflowSteps),
    [baseExtractedWorkflowSteps],
  );

  const { baseInquiryTemplateVersionData } = versionDiff;

  const executeScroll = () => {
    rightColumnRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const groupScrollItemsSharedProps = {
    isActive: false,
    onClick: () => executeScroll(),
    setActiveScrollItemId,
  };
  const configurationNavItems = [
    {
      id: 'fle-general-nav-item-id',
      label: 'General',
      scrollConfig: {
        sectionRef: generalViewRef,
        columnRef: rightColumnRef,
      },
      ...groupScrollItemsSharedProps,
    },
    {
      id: 'fle-fields-nav-item-id',
      label: 'Fields',
      scrollConfig: {
        sectionRef: fieldViewRef,
        columnRef: rightColumnRef,
      },
      ...groupScrollItemsSharedProps,
    },
  ];

  const verificationNavItems = Object.entries(verificationTemplateVersionMap).map(
    ([verificationTemplateVersionId, verificationTemplateVersion]) => {
      return {
        id: 'fle-verification-nav-item-id:' + verificationTemplateVersionId,
        // this should always be defined, but we'll default to 'Verification' as a safety
        label: verificationTemplateVersion?.attributes?.name || 'Verification',
        scrollConfig: {
          sectionRef: verificationTemplateVersionIdToRefMap.current[verificationTemplateVersionId],
          columnRef: rightColumnRef,
        },
        ...groupScrollItemsSharedProps,
      };
    },
  );
  const flowAndExperienceNavItems = [
    {
      id: 'fle-styling-nav-item',
      label: 'Screens',
      scrollConfig: {
        sectionRef: stepsViewRef,
        columnRef: rightColumnRef,
      },
      ...groupScrollItemsSharedProps,
    },
    ...targetActionSequences.map((actionSequence) => ({
      id: 'fle-verification-nav-item-id:' + actionSequence.token,
      // this should always be defined, but we'll default to 'Workflow' as a safety
      label: actionSequence.workflowStep.name || 'Workflow',
      scrollConfig: {
        sectionRef: actionSequenceTokenToRefMap.current[actionSequence.token],
        columnRef: rightColumnRef,
      },
      ...groupScrollItemsSharedProps,
    })),
  ];

  return (
    <ColumnContainer>
      <LeftColumn>
        <NavigationItemsContainer>
          <EditorNavItemScroll
            id='fle-summary-nav-item'
            label='Summary'
            scrollConfig={{
              sectionRef: summaryViewRef,
              columnRef: rightColumnRef,
            }}
            {...groupScrollItemsSharedProps}
          />
          {configurationNavItems.length > 0 && (
            <EditorNavItemGroup
              label='Configurations'
              items={configurationNavItems}
              id='fle-configuration-nav-item'
              activeScrollItemId={activeScrollItemId}
              setActiveScrollItemId={setActiveScrollItemId}
            />
          )}
          {verificationNavItems.length > 0 && (
            <EditorNavItemGroup
              label='Verifications'
              items={verificationNavItems}
              id='fle-verification-nav-item'
              activeScrollItemId={activeScrollItemId}
              setActiveScrollItemId={setActiveScrollItemId}
            />
          )}
          <EditorNavItemGroup
            label='Flow and Experience'
            items={flowAndExperienceNavItems}
            id='fle-flow-and-experience-nav-item'
            activeScrollItemId={activeScrollItemId}
            setActiveScrollItemId={setActiveScrollItemId}
          />
        </NavigationItemsContainer>
      </LeftColumn>
      <RightColumn ref={rightColumnRef}>
        <VersionDiffNavigationContext.Provider
          value={{
            stepsViewRef,
            actionSequenceTokenToRefMap,
            verificationTemplateVersionIdToRefMap,
            selectedStepDifferStep,
            setSelectedStepDifferStep,
          }}
        >
          <BiometricsConsentTextBanners inquiryTemplateVersion={inquiryTemplateVersion} />
          <NavigationDiv ref={summaryViewRef}>
            <InquiryTemplateVersionDiffSummary
              versionDiff={versionDiff}
              inquiryTemplateVersion={inquiryTemplateVersion}
              targetWorkflowVersionMap={workflowVersionMap}
              baseWorkflowVersionMap={baseInquiryTemplateVersionData.workflowVersions}
              targetStepsTree={targetStepsTree}
              baseStepsTree={baseStepsTree}
            />
          </NavigationDiv>
          <NavigationDiv ref={generalViewRef}>
            <InquiryTemplateVersionDiffGeneral
              versionDiff={versionDiff}
              inquiryTemplateVersion={inquiryTemplateVersion}
            />
          </NavigationDiv>
          <NavigationDiv ref={fieldViewRef}>
            <InquiryTemplateVersionDiffField
              versionDiff={versionDiff}
              inquiryTemplateVersion={inquiryTemplateVersion}
            />
          </NavigationDiv>
          <NavigationDiv>
            <InquiryTemplateVersionDiffVerification
              versionDiff={versionDiff}
              verificationTemplateVersionMap={verificationTemplateVersionMap}
              verificationTemplateVersionIdToRefMap={verificationTemplateVersionIdToRefMap}
            />
          </NavigationDiv>
          <NavigationDiv ref={stepsViewRef}>
            <InquiryTemplateVersionDiffSteps
              versionDiff={versionDiff}
              targetInquiryTemplateVersion={inquiryTemplateVersion}
              targetStepsPreviewMap={stepsPreviewMap}
              targetPreviewPageMap={previewPageMap}
              baseStepsPreviewMap={baseStepsPreviewMap}
              basePreviewPageMap={basePreviewPageMap}
            />
          </NavigationDiv>
          <NavigationDiv>
            <InquiryTemplateVersionDiffWorkflow
              versionDiff={versionDiff}
              targetWorkflowVersionMap={workflowVersionMap}
              actionSequenceTokenToRefMap={actionSequenceTokenToRefMap}
              baseWorkflowVersionMap={baseInquiryTemplateVersionData.workflowVersions}
              targetActionSequences={targetActionSequences}
              baseActionSequences={baseActionSequences}
              workflowSchema={workflowSchema}
            />
          </NavigationDiv>
        </VersionDiffNavigationContext.Provider>
      </RightColumn>
    </ColumnContainer>
  );
};

interface BaseInquiryTemplateVersionMetadata {
  baseStepsTree: CombinedStepForTree[];
  baseExtractedWorkflowSteps: VersionDiffLinkedWorkflowStep[];
  baseStepsPreviewMap: StepPreviewMap;
  basePreviewPageMap: PreviewPageMap;
}

const generateBaseInquiryTemplateVersionMetadata = (
  baseInquiryTemplateVersionData: BaseInquiryTemplateVersionData | undefined,
  currentLocale: InquiryIso6391LanguageCode,
  isITEInternal: boolean,
): BaseInquiryTemplateVersionMetadata => {
  if (!baseInquiryTemplateVersionData)
    return {
      baseStepsTree: [],
      baseExtractedWorkflowSteps: [],
      baseStepsPreviewMap: {},
      basePreviewPageMap: {},
    };

  const baseStepsPreviewMap: StepPreviewMap = keyBy(
    'name',
    mapStepsConfigToStepsPreview({
      steps: baseInquiryTemplateVersionData.steps,
      stepsPreview: baseInquiryTemplateVersionData.stepsPreview,
      currentLocale: currentLocale,
    }),
  );

  const basePreviewPageMap = generatePages(Object.values(baseStepsPreviewMap));

  const baseExtractedWorkflowSteps = extractActionsAndConditionalNodes(
    baseInquiryTemplateVersionData.workflowVersions,
  );

  const [baseStepsTree] = buildDiffTreeAndGraph({
    steps: baseInquiryTemplateVersionData.steps,
    stepsPreviewMap: baseStepsPreviewMap,
    previewPageMap: basePreviewPageMap,
    extractedWorkflowSteps: baseExtractedWorkflowSteps,
    isITEInternal,
  });

  return { baseStepsTree, baseExtractedWorkflowSteps, baseStepsPreviewMap, basePreviewPageMap };
};

export default InquiryTemplateVisualVersionDiff;
